import { DateInputLocalized } from '@components'

import { DayWrapperRender, Tooltip } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceEditorForm } from '../../../../../../contexts/invoiceEditorFormContext'
import { useInvoiceEditorDocumentType } from '../../../../../../hooks/useInvoiceEditorDocumentType'
import { useInvoiceIssueDateLimits } from '../../../../../../hooks/useInvoiceIssueDateLimits'
import { useIsInvoiceEditorElementRestricted } from '../../../../../../hooks/useIsInvoiceEditorElementRestricted'
import { useUpdateDependentDates } from '../../../../../../hooks/useUpdateDependentDates'
import { getIssueDateFieldLabel } from './utils/getIssueDateFieldLabel'

export const InvoiceIssueDate = () => {
  const { t } = useTranslation()
  const { handleIssueDateChange } = useUpdateDependentDates()

  const { documentType, isInvoice } = useInvoiceEditorDocumentType()
  const { invoiceEditorForm, trackInvoiceEditor } = useInvoiceEditorForm()

  const { FormItem, watch } = invoiceEditorForm
  const invoiceNumberPrefix = watch('invoiceNumberPrefix')

  const { minIssueDate } = useInvoiceIssueDateLimits({ invoiceNumberPrefix, isInvoice })
  const isDisabled = useIsInvoiceEditorElementRestricted('issueDate', 'disabled')

  const label = useMemo(() => getIssueDateFieldLabel(documentType, t), [documentType, t])

  const handleDatePickerOpen = useCallback(() => {
    trackInvoiceEditor('Form - Issue date picker opened')
  }, [trackInvoiceEditor])

  const dayButtonRender = useCallback<DayWrapperRender>(
    ({ children, isFuture, disabled }) => {
      if (isFuture && !disabled) {
        return (
          <Tooltip
            delay={0}
            label={t('external_invoices.editor.form.field.issue_date.date_picker.future_day.tooltip')}
            placement="top"
          >
            {children}
          </Tooltip>
        )
      }

      return children
    },
    [t],
  )

  return (
    <FormItem
      name="issueDate"
      label={label}
      render={({ field: { onChange, ...props } }) => (
        <DateInputLocalized
          {...props}
          disabled={isDisabled}
          dayWrapperRender={dayButtonRender}
          minDate={minIssueDate}
          onChange={(date) => {
            handleIssueDateChange(date)
            onChange(date)
            trackInvoiceEditor('Form - Issue date selected', {
              date,
            })
          }}
          onOpen={handleDatePickerOpen}
          size="sm"
        />
      )}
    />
  )
}
